import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { API_URL } from "./constants";
import "./TeamTab.css";

// Admin-only Team management view. Shows current Admins (read-only — manage
// those in the AWS Console for safety) and Captains (promote / remove).
// Backend gates on isAdmin, so even if a Captain somehow loaded this tab
// nothing here would work.
const TeamTab = () => {
  const { getAuthHeaders } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [captains, setCaptains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [newCaptainEmail, setNewCaptainEmail] = useState("");
  const [busyAction, setBusyAction] = useState("");
  const [actionMessage, setActionMessage] = useState("");

  const loadTeam = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const headers = getAuthHeaders();
      const resp = await fetch(`${API_URL}?type=teamMembers`, {
        method: "GET",
        headers,
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.error || data?.message || `HTTP ${resp.status}`);
      }
      setAdmins(data.admins || []);
      setCaptains(data.captains || []);
    } catch (err) {
      setError(err.message || "Failed to load team members");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadTeam();
  }, [loadTeam]);

  const handlePromote = async (e) => {
    e.preventDefault();
    const email = newCaptainEmail.trim().toLowerCase();
    if (!email) return;
    setBusyAction(`promote:${email}`);
    setActionMessage("");
    try {
      const headers = {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      };
      const resp = await fetch(API_URL, {
        method: "POST",
        headers,
        body: JSON.stringify({ type: "promoteCaptain", email }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.error || data?.message || `HTTP ${resp.status}`);
      }
      setActionMessage(`Promoted ${email} to Captain.`);
      setNewCaptainEmail("");
      await loadTeam();
    } catch (err) {
      setActionMessage(`Promote failed: ${err.message || err}`);
    } finally {
      setBusyAction("");
    }
  };

  const handleDemote = async (member) => {
    const label = member.email || member.username;
    if (!window.confirm(`Remove ${label} from Captains?`)) return;
    setBusyAction(`demote:${member.username}`);
    setActionMessage("");
    try {
      const headers = {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      };
      const resp = await fetch(API_URL, {
        method: "POST",
        headers,
        body: JSON.stringify({
          type: "demoteCaptain",
          username: member.username,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.error || data?.message || `HTTP ${resp.status}`);
      }
      setActionMessage(`Removed ${label} from Captains.`);
      await loadTeam();
    } catch (err) {
      setActionMessage(`Remove failed: ${err.message || err}`);
    } finally {
      setBusyAction("");
    }
  };

  const renderMemberRow = (member, canRemove) => (
    <tr key={member.username}>
      <td>{member.email || member.username}</td>
      <td>{member.name || ""}</td>
      <td className="team-tab__status">{member.status || ""}</td>
      <td className="team-tab__actions">
        {canRemove && (
          <button
            type="button"
            className="team-tab__btn team-tab__btn--danger"
            onClick={() => handleDemote(member)}
            disabled={busyAction === `demote:${member.username}`}
          >
            {busyAction === `demote:${member.username}` ? "Removing…" : "Remove"}
          </button>
        )}
      </td>
    </tr>
  );

  return (
    <div className="team-tab">
      <h2 className="team-tab__title">Team</h2>

      {error && <div className="team-tab__error">Error: {error}</div>}
      {actionMessage && (
        <div className="team-tab__info">{actionMessage}</div>
      )}

      <h3 className="team-tab__section-title">Admins ({admins.length})</h3>
      <p className="team-tab__hint">
        Admin membership is managed in the AWS Cognito console — change it
        carefully (you can lock yourself out).
      </p>
      {loading ? (
        <div className="team-tab__info">Loading…</div>
      ) : (
        <table className="team-tab__table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {admins.length === 0 ? (
              <tr>
                <td colSpan={4} className="team-tab__empty">
                  No one in the Admins group.
                </td>
              </tr>
            ) : (
              admins.map((m) => renderMemberRow(m, false))
            )}
          </tbody>
        </table>
      )}

      <h3 className="team-tab__section-title">Captains ({captains.length})</h3>
      <p className="team-tab__hint">
        Captains can manage events, photos, locations, and post to GroupMe.
        They cannot approve members or see the user database.
      </p>
      {!loading && (
        <table className="team-tab__table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {captains.length === 0 ? (
              <tr>
                <td colSpan={4} className="team-tab__empty">
                  No captains yet. Add one below.
                </td>
              </tr>
            ) : (
              captains.map((m) => renderMemberRow(m, true))
            )}
          </tbody>
        </table>
      )}

      <form className="team-tab__add" onSubmit={handlePromote}>
        <label htmlFor="new-captain-email">Promote a user to Captain</label>
        <div className="team-tab__add-row">
          <input
            id="new-captain-email"
            type="email"
            value={newCaptainEmail}
            onChange={(e) => setNewCaptainEmail(e.target.value)}
            placeholder="user@example.com"
            autoComplete="off"
            required
          />
          <button
            type="submit"
            className="team-tab__btn team-tab__btn--primary"
            disabled={
              !newCaptainEmail.trim() ||
              busyAction === `promote:${newCaptainEmail.trim().toLowerCase()}`
            }
          >
            {busyAction.startsWith("promote:") ? "Adding…" : "Add Captain"}
          </button>
        </div>
        <p className="team-tab__hint">
          The user must already have a Cognito account (they sign up via the
          normal Login page first).
        </p>
      </form>
    </div>
  );
};

export default TeamTab;
